import { motion } from "framer-motion";
import { Heart, Users, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCMS } from "@/lib/CMSContext";
import { SectionTracker } from "./SectionTracker";

const STORY_IMAGE = "https://images.unsplash.com/photo-1609220136736-443140cffec6?w=800&q=80";

const OurStory = () => {
  const { t } = useLanguage();
  const { cms } = useCMS();
  const storyImg = cms.images.story || STORY_IMAGE;

  return (
    <SectionTracker id="our-story">
    <section id="our-story" className="py-20 md:py-28 bg-secondary/50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">{t("story_label")}</span>
          <h2 className="font-display text-3xl md:text-4xl text-foreground mt-3 mb-4">
            {t("story_title")}
          </h2>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-10 items-center"
          >
            <div className="order-2 md:order-1">
              <p className="text-foreground leading-relaxed mb-4">
                {t("story_p1")}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t("story_p2")}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t("story_p3")}
              </p>
            </div>

            <div className="order-1 md:order-2">
              <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-card)] aspect-[4/3]">
                <img src={storyImg} alt="Our team of caregivers supporting elderly individuals with compassion" className="w-full h-full object-cover" />
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6 mt-12"
          >
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-card rounded-xl p-6 shadow-[var(--shadow-soft)]"
              >
                <Heart className="w-8 h-8 text-accent mb-3" />
                <h3 className="font-display text-lg text-foreground mb-1">{t("story_card1_title")}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{t("story_card1_desc")}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-card rounded-xl p-6 shadow-[var(--shadow-soft)]"
              >
                <Users className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-display text-lg text-foreground mb-1">{t("story_card2_title")}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{t("story_card2_desc")}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-card rounded-xl p-6 shadow-[var(--shadow-soft)]"
              >
                <Globe className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-display text-lg text-foreground mb-1">{t("story_card3_title")}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{t("story_card3_desc")}</p>
              </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
    </SectionTracker>
  );
};

export default OurStory;
